import { useMemo, useState } from 'react';
import { useWorkspace } from '@/app/providers/workspace-provider';
import { createFollowUpRepository } from '@/data/repositories/follow-up-repository';
import type { ReminderChannel, Uuid } from '@/data/db';
import { Button } from '@/components/primitives/button';
import { Dialog } from '@/components/primitives/dialog';
import { Field, Select, TextArea, TextInput } from '@/components/primitives/field';
import { useToast } from '@/components/feedback/toast';
import { useActiveContacts } from '@/features/contacts/use-contacts';
import { nowLocalDateTime } from '@/lib/clock';

export const REMINDER_CHANNEL_LABELS: Record<ReminderChannel, string> = {
  in_person: '當面',
  phone: '電話',
  message: '訊息',
  email: '電子郵件',
  meeting: '會議',
  other: '其他',
};

interface SupervisorReminderEditorProps {
  taskId: Uuid;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 再提醒一次時預填。 */
  defaults?: {
    supervisorContactId?: Uuid;
    channel?: ReminderChannel;
    message?: string;
  };
}

/** 主管提醒是正式資料，不依賴看板欄位名稱判斷（spec 12）。 */
export function SupervisorReminderEditor({
  taskId,
  open,
  onOpenChange,
  defaults,
}: SupervisorReminderEditorProps) {
  const { db, save } = useWorkspace();
  const toast = useToast();
  const repository = useMemo(() => createFollowUpRepository(db), [db]);
  const contacts = useActiveContacts();

  const [supervisorContactId, setSupervisorContactId] = useState(
    defaults?.supervisorContactId ?? '',
  );
  const [remindAtLocal, setRemindAtLocal] = useState(() => {
    const now = nowLocalDateTime();
    return now;
  });
  const [channel, setChannel] = useState<ReminderChannel>(defaults?.channel ?? 'in_person');
  const [message, setMessage] = useState(defaults?.message ?? '');
  const [error, setError] = useState<string | null>(null);

  const contactOptions = (contacts ?? []).map((contact) => ({
    value: contact.id,
    label: contact.jobTitle ? `${contact.name}（${contact.jobTitle}）` : contact.name,
  }));

  const resolvedContactId = supervisorContactId || (contactOptions[0]?.value ?? '');

  async function submit() {
    if (!resolvedContactId) {
      setError('請先到通訊錄新增一位主管。');
      return;
    }
    if (!remindAtLocal) {
      setError('請選一個提醒時間。');
      return;
    }
    if (!message.trim()) {
      setError('寫一句提醒重點，之後才知道當初要說什麼。');
      return;
    }

    try {
      await save.run(async () => {
        await repository.createReminder({
          taskId,
          supervisorContactId: resolvedContactId,
          remindAtLocal,
          message: message.trim(),
          channel,
        });
      });
      toast.show({ message: '已建立主管提醒', detail: message.trim(), tone: 'success' });
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : '沒有存成功，可以再試一次。');
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="設定主管提醒"
      description="時間到了會出現在今日桌面的第一區。提醒完之後可以直接記錄後續。"
      size="md"
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button variant="primary" size="sm" onClick={() => void submit()}>
            建立提醒
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="要提醒誰" error={error}>
          {({ id, describedBy }) =>
            contactOptions.length === 0 ? (
              <p
                id={describedBy}
                className="rounded-[var(--radius-control-lg)] bg-surface-sunken px-4 py-3 text-[0.875rem] text-text-muted"
              >
                通訊錄裡還沒有人。先到通訊錄新增主管，再回來設定提醒。
              </p>
            ) : (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={resolvedContactId}
                onValueChange={setSupervisorContactId}
                options={contactOptions}
              />
            )
          }
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="提醒時間">
            {({ id }) => (
              <TextInput
                id={id}
                type="datetime-local"
                value={remindAtLocal}
                onChange={(event) => setRemindAtLocal(event.target.value)}
              />
            )}
          </Field>

          <Field label="用什麼方式">
            {({ id }) => (
              <Select
                id={id}
                value={channel}
                onValueChange={(value) => setChannel(value as ReminderChannel)}
                options={Object.entries(REMINDER_CHANNEL_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
            )}
          </Field>
        </div>

        <Field label="提醒重點">
          {({ id }) => (
            <TextArea
              id={id}
              value={message}
              placeholder="例如：請主管確認季報的第三頁數字"
              onChange={(event) => {
                setMessage(event.target.value);
                if (error) setError(null);
              }}
            />
          )}
        </Field>
      </div>
    </Dialog>
  );
}
